import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { CSSTransition } from 'react-transition-group';
import { Manager, Reference } from 'react-popper';
import { font, color, spacing } from '@metro-ui/tokens/js';
import { ChevronRight } from '../../icons/icons/ChevronRight';
import { Link, LinkPropTypes } from '../../Link';
import { Indicator } from '../../Indicator';
import { Strong } from '../../Typography';
import { SidebarContext } from './SidebarContext';
import { SidebarDelayedFadeInContainer, animationClass, } from './SidebarAnimations';
var IconBadge = styled(Indicator)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: absolute;\n  top: -0.5rem;\n  right: 1.25rem;\n"], ["\n  position: absolute;\n  top: -0.5rem;\n  right: 1.25rem;\n"])));
var IconContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: relative;\n  padding: 0 1.625rem;\n  flex-shrink: 0;\n"], ["\n  position: relative;\n  padding: 0 1.625rem;\n  flex-shrink: 0;\n"])));
var handleOpen = function (openHandler) {
    return function (e) {
        switch (e.key) {
            // case 'ArrowRight':
            // case 'Right':
            case 'Enter':
            case ' ':
                return openHandler && openHandler(e);
            default:
        }
    };
};
var handleClose = function (closeHandler) {
    return function (e) {
        switch (e.key) {
            case 'Escape':
            case 'Esc':
                return closeHandler && closeHandler(e);
            default:
        }
    };
};
var onClick = function (openHandler, defaultHandler) {
    return function (e) {
        if (openHandler)
            openHandler(e);
        if (defaultHandler && typeof defaultHandler === 'function')
            defaultHandler(e);
    };
};
var MenuItem = React.forwardRef(function (_a, ref) {
    var Component = _a.Component, Icon = _a.Icon, title = _a.title, level = _a.level, notifications = _a.notifications, isActive = _a.isActive, hasSubMenu = _a.hasSubMenu, hasDrawer = _a.hasDrawer, hasFlyout = _a.hasFlyout, isOpen = _a.isOpen, linkAttributes = _a.linkAttributes, onOpenSubMenu = _a.onOpenSubMenu, onCloseSubMenu = _a.onCloseSubMenu, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["Component", "Icon", "title", "level", "notifications", "isActive", "hasSubMenu", "hasDrawer", "hasFlyout", "isOpen", "linkAttributes", "onOpenSubMenu", "onCloseSubMenu", "className", "children"]);
    var props = { Icon: Icon, title: title, level: level, notifications: notifications, hasSubMenu: hasSubMenu, hasDrawer: hasDrawer };
    return (React.createElement(Manager, null,
        React.createElement(Reference, null, function (_a) {
            var popperRef = _a.ref;
            return (
            /* We need to capture the Key events fired from within the children */
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
            React.createElement("div", { ref: popperRef, onKeyDown: handleClose(onCloseSubMenu) },
                React.createElement(Link, __assign({ tabIndex: -1 }, htmlAttributes, linkAttributes, { onClick: onClick(onOpenSubMenu, htmlAttributes.onClick ||
                        (linkAttributes && linkAttributes.onClick)), onKeyDown: handleOpen(onOpenSubMenu), role: "menuitem", ref: ref, "aria-label": title, "aria-haspopup": hasSubMenu || hasFlyout || hasDrawer, "aria-expanded": isOpen, className: className, isNotUnderlined: true }), !!Component && React.createElement(Component, __assign({}, props))),
                children && (
                /* We use CSSTransition only when we have a sub-menu to which to apply this transitions */
                React.createElement(CSSTransition, { appear: true, mountOnEnter: true, unmountOnExit: true, classNames: animationClass, timeout: {
                        appear: 400,
                        enter: 400,
                        exit: hasSubMenu ? 0 : 400,
                    }, in: isOpen }, hasSubMenu && React.isValidElement(children)
                    ? React.cloneElement(children, {
                        onCloseSubMenu: onCloseSubMenu,
                    })
                    : children))));
        })));
});
var asMenuItem = function (Component) {
    return React.forwardRef(function (props, ref) { return (React.createElement(MenuItem, __assign({ Component: Component, ref: ref }, props))); });
};
var MenuItemLevel1 = styled(asMenuItem(function (_a) {
    var Icon = _a.Icon, title = _a.title, notifications = _a.notifications, hasSubMenu = _a.hasSubMenu, hasDrawer = _a.hasDrawer;
    return (React.createElement(SidebarContext.Consumer, null, function (_a) {
        var isExpanded = _a.isExpanded;
        return (React.createElement(React.Fragment, null,
            Icon && (React.createElement(IconContainer, null,
                React.createElement(Icon, { size: 1.167 }),
                notifications && (React.createElement(IconBadge, { size: "small" }, notifications)))),
            isExpanded && (React.createElement(SidebarDelayedFadeInContainer, null,
                React.createElement(Strong, null, title),
                (hasSubMenu || hasDrawer) && (React.createElement(ChevronRight, { size: 1.3335, style: {
                        marginLeft: 'auto',
                        marginRight: spacing['x-small'],
                    } }))))));
    }));
}))(function (_a) {
    var isActive = _a.isActive;
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    position: relative;\n    height: 4rem;\n    flex-shrink: 0;\n    width: 100%;\n\n    /* Replace with color=\"inverted\" when merged into master */\n    color: white;\n    &:hover,\n    &:active,\n    &:focus {\n      color: white;\n    }\n\n    &:hover,\n    &:focus {\n      background-color: ", ";\n    }\n\n    ", "\n  "], ["\n    position: relative;\n    height: 4rem;\n    flex-shrink: 0;\n    width: 100%;\n\n    /* Replace with color=\"inverted\" when merged into master */\n    color: white;\n    &:hover,\n    &:active,\n    &:focus {\n      color: white;\n    }\n\n    &:hover,\n    &:focus {\n      background-color: ", ";\n    }\n\n    ", "\n  "])), color.interaction, isActive && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      background-color: ", ";\n    "], ["\n      background-color: ", ";\n    "])), color.interaction));
});
var SubMenuItem = styled(asMenuItem(function (_a) {
    var title = _a.title, hasSubMenu = _a.hasSubMenu;
    return (React.createElement(React.Fragment, null,
        React.createElement("span", null, title),
        hasSubMenu && (React.createElement(ChevronRight, { size: 1.3335, style: {
                marginLeft: 'auto',
            } }))));
}))(function (_a) {
    var isActive = _a.isActive;
    return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    position: relative;\n    width: 100%;\n    height: 2.5rem;\n    flex-shrink: 0;\n    padding: 0 ", ";\n    /* font-weight: ", "; */\n\n    color: ", ";\n\n    &:hover,\n    &:focus {\n      color: ", ";\n      background-color: ", ";\n      box-shadow: none;\n    }\n\n    ", "\n  "], ["\n    position: relative;\n    width: 100%;\n    height: 2.5rem;\n    flex-shrink: 0;\n    padding: 0 ", ";\n    /* font-weight: ", "; */\n\n    color: ", ";\n\n    &:hover,\n    &:focus {\n      color: ", ";\n      background-color: ", ";\n      box-shadow: none;\n    }\n\n    ", "\n  "])), spacing.small, font.weight.normal, color.brand.primary.default, color.interaction, color.palette.blue.tint[95], isActive && css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n      font-weight: ", ";\n      background-color: ", ";\n    "], ["\n      font-weight: ", ";\n      background-color: ", ";\n    "])), font.weight.heavy, color.palette.blue.tint[95]));
});
export var SidebarMenuItem = styled(React.forwardRef(function (props, ref) {
    return props.level === 1 || props.level === undefined ? (React.createElement(MenuItemLevel1, __assign({}, props, { ref: ref }))) : (React.createElement(SubMenuItem, __assign({}, props, { ref: ref })));
}))();
SidebarMenuItem.propTypes = {
    Icon: PropTypes.func,
    title: PropTypes.string.isRequired,
    notifications: PropTypes.number,
    isActive: PropTypes.bool,
    hasSubMenu: PropTypes.bool,
    hasDrawer: PropTypes.bool,
    hasFlyout: PropTypes.bool,
    isOpen: PropTypes.bool,
    // @ts-ignore
    linkAttributes: PropTypes.shape(LinkPropTypes),
    onOpenSubMenu: PropTypes.func,
    onCloseSubMenu: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.node,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
//# sourceMappingURL=SidebarMenuItem.js.map